import {
  generateBreadcrumbSchema,
  generateCollectionSchema,
  generateNewsArticleSchema,
  generateOrganizationSchema,
  generateProductCatalogSchema,
  generateProductSchema,
  generateWebsiteSchema,
} from '@/lib/seo/schema/generator'
import { getRouteInfo, isLandingPage } from '@/lib/seo/schema/util'
import { SchemaContent } from '@/types/schema'
import { useMemo } from 'react'
import { JsonLd } from './json-ld'

interface SchemaManagerProps {
  content?: SchemaContent
  pathname: string
  isMainLayout?: boolean
}

const EXCLUDE_PATHNAMES = ['product', 'collection']

export function SchemaManager({
  content,
  pathname,
  isMainLayout,
}: SchemaManagerProps) {
  // Generate schemas based on route
  const schemas = useMemo(() => {
    const result = []
    const { routeName } = getRouteInfo(pathname)

    // Add breadcrumb schema for all routes
    result.push(generateBreadcrumbSchema(routeName, content))

    // Add organization schema for home page or landing pages
    if (routeName === 'home' || isLandingPage(pathname)) {
      result.push(generateOrganizationSchema())
      result.push(generateWebsiteSchema())
    }

    // Add route-specific schemas
    if (routeName === 'product' && content) {
      result.push(generateProductSchema(content))
    } else if (routeName === 'collection' && content) {
      result.push(generateCollectionSchema(content))
    } else if (
      ['84rising', 'cm24', 'filter.index'].includes(routeName) &&
      content
    ) {
      result.push(generateProductCatalogSchema(content))
    } else if (routeName === 'single-article' && content) {
      result.push(generateNewsArticleSchema(content))
    }

    return result
  }, [pathname, content])

  if (isMainLayout) {
    const { routeName } = getRouteInfo(pathname)

    if (EXCLUDE_PATHNAMES.includes(routeName)) {
      return null
    }
  }

  return (
    <>
      {schemas.map((schema, index) => (
        <JsonLd key={`schema-${index}`} data={schema} />
      ))}
    </>
  )
}
